import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ExecutionLog from "./ExecutionLog.jsx";

const DEFAULT_WIDTH = 360;
const PANEL_RIGHT_MARGIN = 16;

function ChatPanel({
  messages,
  logs,
  isRunning,
  hasApiKey,
  onSubmit,
  onAbort,
  onReset,
  onOpenSettings,
  onClose,
  onWidthChange,
}) {
  const [input, setInput] = useState("");
  const [isResizing, setIsResizing] = useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();
    const content = input.trim();
    if (!content || isRunning) return;
    setInput("");
    onSubmit(content);
  };

  const handleResizeStart = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsResizing(true);
  };

  const handleResizeMove = (event) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    onWidthChange(window.innerWidth - PANEL_RIGHT_MARGIN - event.clientX);
  };

  const handleResizeEnd = (event) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    setIsResizing(false);
    onWidthChange(window.innerWidth - PANEL_RIGHT_MARGIN - event.clientX, {
      commit: true,
    });
  };

  return (
    <section className={`chat-panel${isResizing ? " is-resizing" : ""}`}>
      <div
        className="chat-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="パネル幅を変更"
        title="ドラッグで幅を変更（ダブルクリックで既定幅）"
        onPointerDown={handleResizeStart}
        onPointerMove={handleResizeMove}
        onPointerUp={handleResizeEnd}
        onPointerCancel={handleResizeEnd}
        onDoubleClick={() => onWidthChange(DEFAULT_WIDTH, { commit: true })}
      />
      <div className="panel-heading">
        <div>
          <p className="section-label">AI Agent</p>
          <h2 className="panel-title">PortWatch エージェント</h2>
        </div>
        <div className="panel-heading-actions">
          <button
            type="button"
            className="ghost-button"
            onClick={onReset}
            disabled={isRunning}
          >
            新しい会話
          </button>
          <span className={`status ${isRunning ? "is-running" : ""}`}>
            {isRunning ? "実行中" : "待機中"}
          </span>
          <button
            type="button"
            className="panel-collapse"
            onClick={onClose}
            title="閉じる"
            aria-label="AIエージェントを閉じる"
          >
            »
          </button>
        </div>
      </div>

      <div className="message-list" aria-live="polite">
        {messages.map((message) => {
          const isProgress = message.kind === "progress";
          const isNotice = message.kind === "notice";
          const label =
            message.role === "user"
              ? "You"
              : isProgress
                ? "途中経過"
                : isNotice
                  ? "お知らせ"
                  : "Agent";

          return (
            <article
              className={`message message-${message.role}${
                isProgress ? " message-progress" : ""
              }${isNotice ? " message-notice" : ""}`}
              key={message.id}
            >
              <span>{label}</span>
              {message.role === "assistant" && !isProgress && !isNotice ? (
                <div className="message-markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </ReactMarkdown>
                </div>
              ) : (
                <p>{message.content}</p>
              )}
            </article>
          );
        })}
        {isRunning && (
          <div className="message-spinner" role="status" aria-label="処理中">
            <span className="spinner" aria-hidden="true" />
            <span>処理中…</span>
          </div>
        )}
      </div>

      <ExecutionLog logs={logs} />

      <form className="prompt-form" onSubmit={handleSubmit}>
        <label htmlFor="prompt">分析したい内容</label>
        <textarea
          id="prompt"
          value={input}
          rows={3}
          placeholder="例: シンガポール港の直近3ヶ月の入港数トレンドを分析して"
          onChange={(event) => setInput(event.target.value)}
        />
        <div className="prompt-actions">
          <p>
            {hasApiKey
              ? "地点はエージェントが検索します。"
              : "右上の API 設定で Claude APIキーを入力してください。"}
          </p>
          {isRunning ? (
            <button type="button" className="ghost-button" onClick={onAbort}>
              中断
            </button>
          ) : hasApiKey ? (
            <button
              type="submit"
              className="primary-button"
              disabled={!input.trim()}
            >
              分析を開始
            </button>
          ) : (
            <button
              type="button"
              className="primary-button"
              onClick={onOpenSettings}
            >
              API設定を開く
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

export default ChatPanel;
